import { ofType } from "redux-observable";
import { debounceTime, map, switchMap, filter, take } from "rxjs/operators";
import { of } from "rxjs";
import JobProApi from "../../dependencies/JobProApi";
import config from "config.json";

const PURCHASE_ORDER_FIELDS =
  "id,office(id,name),name,poNumber,shipTo,status(name),supplier(name),deliveryOrder";
const INVENTORY_PURCHASE_ORDER_FIELDS =
  "id,office(id,name),name,poNumber,shipTo,status,supplier(name),deliveryOrder";

// Action types
const FETCH_AVAILABLE_PURCHASE_ORDERS = "FETCH_AVAILABLE_PURCHASE_ORDERS";
const FETCH_AVAILABLE_PURCHASE_ORDERS_COMPLETE =
  "FETCH_AVAILABLE_PURCHASE_ORDERS_COMPLETE";
const UPDATE_PURCHASE_ORDER_SEARCH_CRITERIA =
  "UPDATE_PURCHASE_ORDER_SEARCH_CRITERIA";

const DEFAULT_STATE = {
  form: {
    offices: [],
    statuses: [],
    suppliers: [],
  },
  loading: false,
  purchaseOrders: [],
};

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case FETCH_AVAILABLE_PURCHASE_ORDERS:
      return {
        ...state,
        loading: true,
        purchaseOrders: [],
      };
    case FETCH_AVAILABLE_PURCHASE_ORDERS_COMPLETE:
      return {
        ...state,
        ...payload,
        loading: false,
      };
    case UPDATE_PURCHASE_ORDER_SEARCH_CRITERIA:
      return {
        ...state,
        form: {
          ...state.form,
          ...payload,
        },
      };
    default:
      return state;
  }
}

// Action creators
export const fetchAvailablePurchaseOrders = () => ({
  type: FETCH_AVAILABLE_PURCHASE_ORDERS,
});

const fetchAvailablePurchaseOrdersComplete = (purchaseOrders) => ({
  type: FETCH_AVAILABLE_PURCHASE_ORDERS_COMPLETE,
  payload: { purchaseOrders },
});

export const updateSearchCriteria = (criteria) => ({
  type: UPDATE_PURCHASE_ORDER_SEARCH_CRITERIA,
  payload: criteria,
});

export const openPurchaseOrder = (purchaseOrderId) => {
  window.open(
    `${config.versaItRoot}display.asp?key=6083&fo=menu&rm=page&xID=${purchaseOrderId}`
  );
};

export const openInventoryPurchaseOrder = (purchaseOrderId) => {
  window.open(
    `${config.versaItRoot}display.asp?key=6043&fo=menu&rm=page&xID=${purchaseOrderId}`
  );
};

// Epics
export const fetchAvailablePurchaseOrdersEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      FETCH_AVAILABLE_PURCHASE_ORDERS,
      UPDATE_PURCHASE_ORDER_SEARCH_CRITERIA
    ),
    debounceTime(300),
    switchMap(() => {
      // Wait for tokenInfo to finish loading before proceeding
      if (state$.value.tokenInfo?.loading) {
        return state$.pipe(
          filter((state) => !state.tokenInfo?.loading),
          take(1),
          map(() => state$.value)
        );
      }
      // If tokenInfo is already loaded, proceed immediately
      return of(state$.value);
    }),
    switchMap((state) => {
      const { offices, query, statuses, suppliers } =
        state.searchPurchaseOrders.form;
      const { tokenInfo } = state;

      const hasInventoryScope =
        tokenInfo?.scope?.split(" ").includes("inventory:on") || false;

      const criteria = {};
      if (query) {
        criteria.query = query;
      }
      if (offices.length) {
        criteria["office.id"] = offices.map((o) => o.id);
      }
      if (statuses.length) {
        criteria["status.id"] = statuses.map((s) => s.id);
      }
      if (suppliers.length) {
        criteria["supplier.id"] = suppliers.map((s) => s.id);
      }

      // Fetch both regular and inventory purchase orders
      const regularPOsPromise = JobProApi.purchaseOrders.search(criteria, {
        fields: PURCHASE_ORDER_FIELDS,
        limit: 50,
      });

      // Conditionally fetch inventory purchase orders
      const promises = [regularPOsPromise];

      if (hasInventoryScope) {
        const inventoryPOsPromise = JobProApi.inventoryPurchaseOrders.search(
          criteria,
          {
            fields: INVENTORY_PURCHASE_ORDER_FIELDS,
            limit: 50,
          }
        );
        promises.push(inventoryPOsPromise);
      }

      return Promise.all(promises).then((results) => {
        const [regularPOs, inventoryPOs = []] = results;

        // Mark regular POs
        const normalizedRegularPOs = regularPOs.map((po) => ({
          ...po,
          isInventoryPO: false,
          deliveryOrder: po.deliveryOrder || 1,
        }));

        // Mark inventory POs if available
        const normalizedInventoryPOs = hasInventoryScope
          ? inventoryPOs.map((po) => ({
              ...po,
              isInventoryPO: true,
              deliveryOrder: po.deliveryOrder || 1,
              status: po.status ? { name: po.status } : { name: "Unknown" },
            }))
          : [];

        // Combine and sort by id descending, then by deliveryOrder
        const allPurchaseOrders = [
          ...normalizedRegularPOs,
          ...normalizedInventoryPOs,
        ].sort((a, b) => b.id - a.id || a.deliveryOrder - b.deliveryOrder);

        return allPurchaseOrders;
      });
    }),
    map(fetchAvailablePurchaseOrdersComplete)
  );
